import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, XCircle } from "lucide-react";
import Seo from "@/components/Seo";
import AuthShell from "@/components/AuthShell";
import CTAButton from "@/components/CTAButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) { setError("This invitation link is invalid."); return; }
    api.get(`/invites/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || "This invitation is invalid or already used."));
  }, [token]);

  const accept = async () => {
    if (user === false) {
      navigate(`/login?next=${encodeURIComponent(`/accept-invite?token=${token}`)}`);
      return;
    }
    setAccepting(true);
    try {
      await api.post("/invites/accept", { token });
      await checkAuth();
      toast.success("You've joined the workspace!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not accept invitation.");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <>
      <Seo title="Accept invitation" path="/accept-invite" />
      <AuthShell title={error ? "Invitation" : "Join workspace"} subtitle={error ? "" : "You've been invited to collaborate."}>
        {error ? (
          <div className="flex flex-col items-center gap-4 text-center" data-testid="invite-error">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 text-destructive"><XCircle className="h-7 w-7" /></span>
            <p className="text-sm text-muted-foreground">{error}</p>
            <CTAButton to="/" variant="outline" size="md">Back home</CTAButton>
          </div>
        ) : invite ? (
          <div className="flex flex-col items-center gap-5 text-center" data-testid="invite-accept">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full brand-gradient-bg text-white"><Users className="h-7 w-7" /></span>
            <div>
              <p className="font-display text-lg font-semibold">{invite.organization_name}</p>
              <p className="mt-1 text-sm text-muted-foreground">You're invited as <span className="font-medium capitalize text-foreground">{invite.invite?.role || "member"}</span>.</p>
            </div>
            {user === false && <p className="text-xs text-muted-foreground">You'll be asked to log in or sign up first.</p>}
            <CTAButton as="button" onClick={accept} variant="gradient" size="md" disabled={accepting} data-testid="accept-invite-btn">
              {accepting ? "Joining…" : "Accept & join"}
            </CTAButton>
            <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">Not now</Link>
          </div>
        ) : (
          <div className="flex justify-center py-6"><div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" /></div>
        )}
      </AuthShell>
    </>
  );
}
